import { mountAgentMark, type MountedAgentMark } from '@flexiple-buildd/ai/mount-agent-mark';

// Controls the #app-loader logo splash painted by index.html. It leaves once the
// router has committed and no layout route is still waiting (see LayoutPending),
// so the first thing under it is the shell with the page's own skeleton.
// It stays up through the OAuth callback routes (/oauth/*): they end in a
// full-page window.location.replace after the code exchange. Callback error paths
// soft-navigate away instead, so they must call hideAppLoader themselves.
const MIN_LOADER_MS = Number(document.getElementById('app-loader')?.dataset.minMs) || 600;
const FADE_MS = 320;
const FONT_WAIT_MS = 1500;
// Nothing has rendered by now: something broke, so get out of the way.
const LOADER_SAFETY_MS = 5000;
// A layout is still waiting on the session: keep the splash, but never forever.
const LOADER_HOLD_CAP_MS = 15000;
const loaderStart = performance.now();

let loaderHidden = false;
let routerCommitted = false;
const holds = { layout: 0, page: 0 };
let splashMark: MountedAgentMark | null = null;

export function mountSplashMark() {
	const host = document.getElementById('loader-dexter');
	if (!host) return;
	splashMark = mountAgentMark(host, {
		size: host.offsetWidth,
		scale: Number(host.dataset.scale) || 1,
	});
}

// index.html loops Dexter's show in beats and marks the whole-logo rest at the end
// of each one; the fade has to fit inside that rest, and the first beat always plays.
function untilWholeLogo(loader: HTMLElement) {
	const { beatStart, beatMs, restFrom, restTo } = loader.dataset;
	const beat = Number(beatMs);
	if (!beat) return 0;
	const into = performance.now() - Number(beatStart);
	const from = Number(restFrom);
	if (into < from) return from - into;
	const phase = into % beat;
	if (phase >= from && phase <= Number(restTo) - FADE_MS - 40) return 0;
	return phase < from ? from - phase : beat - phase + from;
}

// The splash is fixed over the whole viewport at the same depth as the top
// loading bar, so anything that wants to know whether it has the screen to
// itself has to wait for this.
let splashGone = !document.getElementById('app-loader');
const splashWatchers = new Set<() => void>();

function markSplashGone() {
	if (splashGone) return;
	splashGone = true;
	for (const notify of splashWatchers) notify();
	splashWatchers.clear();
}

export function isAppLoaderGone() {
	return splashGone;
}

/** Subscribes to the splash leaving the DOM. Shaped for useSyncExternalStore. */
export function watchAppLoader(onGone: () => void) {
	if (splashGone) return () => {};
	splashWatchers.add(onGone);
	return () => {
		splashWatchers.delete(onGone);
	};
}

export function hideAppLoader() {
	if (loaderHidden) return;
	loaderHidden = true;
	const loader = document.getElementById('app-loader');
	if (!loader) {
		markSplashGone();
		return;
	}
	// Wait for the web font too, or text reflows under the user when it swaps in.
	// Bounded, so a font that never arrives can't hold the splash.
	const fontsReady = Promise.race([
		document.fonts.ready,
		new Promise((resolve) => window.setTimeout(resolve, FONT_WAIT_MS)),
	]);
	void fontsReady.then(() => {
		const elapsed = performance.now() - loaderStart;
		const remaining = Math.max(0, MIN_LOADER_MS - elapsed);
		const leave = () => {
			loader.classList.add('is-hiding');
			window.setTimeout(() => {
				splashMark?.destroy();
				splashMark = null;
				loader.remove();
				markSplashGone();
			}, FADE_MS);
		};
		window.setTimeout(() => {
			window.setTimeout(leave, untilWholeLogo(loader));
		}, remaining);
	});
}

function onOauthCallback() {
	return window.location.pathname.startsWith('/oauth');
}

// No-op on /oauth/* so the splash covers the code exchange.
export function autoHideAppLoader() {
	if (onOauthCallback()) return;
	hideAppLoader();
}

// Layout effects of one commit all run before this, so a hold taken in the same
// commit as the router's first one is counted.
function hideWhenFrameReady() {
	queueMicrotask(() => {
		if (routerCommitted && holds.layout + holds.page === 0) autoHideAppLoader();
	});
}

/** The router committed its first matches; the splash can leave once nothing holds it. */
export function markRouterCommitted() {
	routerCommitted = true;
	hideWhenFrameReady();
}

/**
 * Keeps the splash up while a route waits, so it never gives way to a frame that
 * isn't the page's own. A layout (the session, the account) holds it up to the
 * cap; a page on the generic skeleton only until the safety timeout.
 * Returns the release; shaped for an effect.
 */
export function holdAppLoader(kind: keyof typeof holds = 'layout') {
	holds[kind] += 1;
	let released = false;
	return () => {
		if (released) return;
		released = true;
		holds[kind] -= 1;
		hideWhenFrameReady();
	};
}

// Fallbacks for a first render that never happens, gated the same way: a slow
// OAuth exchange keeps its cover and resolves the splash itself.
export function scheduleAppLoaderSafetyHide() {
	window.setTimeout(() => {
		if (holds.layout === 0) autoHideAppLoader();
	}, LOADER_SAFETY_MS);
	window.setTimeout(autoHideAppLoader, LOADER_HOLD_CAP_MS);
}
